// Utility functions for handling post-authentication redirects

const REDIRECT_KEY = 'postAuthRedirect';
const REDIRECT_EXPIRY_KEY = 'postAuthRedirectExpiry';
const REDIRECT_EXPIRY_HOURS = 24; // Redirect expires after 24 hours

/**
 * Validates if a redirect URL is safe and allowed
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is valid and safe
 */
export const isValidRedirect = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Must be a relative path (starts with /)
    if (!url.startsWith('/')) return false;

    return true;
};

/**
 * Stores a redirect URL in sessionStorage with expiry
 * @param {string} url - The URL to store
 * @returns {boolean} - True if stored successfully
 */
export const storeRedirect = (url) => {
    if (!isValidRedirect(url)) {
        console.warn('Invalid redirect URL:', url);
        return false;
    }
    
    try {
        const expiryTime = new Date().getTime() + (REDIRECT_EXPIRY_HOURS * 60 * 60 * 1000);
        sessionStorage.setItem(REDIRECT_KEY, url);
        sessionStorage.setItem(REDIRECT_EXPIRY_KEY, expiryTime.toString());
        return true;
    } catch (error) {
        console.error('Error storing redirect:', error);
        return false;
    }
};

/**
 * Retrieves the stored redirect URL if valid and not expired
 * @returns {string|null} - The redirect URL or null if not found/expired/invalid
 */
export const getStoredRedirect = () => {
    try {
        const redirect = sessionStorage.getItem(REDIRECT_KEY);
        const expiry = sessionStorage.getItem(REDIRECT_EXPIRY_KEY);
        
        if (!redirect || !expiry) return null;
        
        // Check if expired
        const expiryTime = parseInt(expiry, 10);
        if (new Date().getTime() > expiryTime) {
            clearStoredRedirect();
            return null;
        }
        
        // Validate again for security
        if (!isValidRedirect(redirect)) {
            clearStoredRedirect();
            return null;
        }
        
        return redirect;
    } catch (error) {
        console.error('Error retrieving redirect:', error);
        return null;
    }
};

/**
 * Clears the stored redirect from sessionStorage
 */
export const clearStoredRedirect = () => {
    try {
        sessionStorage.removeItem(REDIRECT_KEY);
        sessionStorage.removeItem(REDIRECT_EXPIRY_KEY);
    } catch (error) {
        console.error('Error clearing redirect:', error);
    }
};

/**
 * Extracts redirect parameter from URL search params
 * @param {URLSearchParams|string} searchParams - The search params or search string
 * @returns {string|null} - The decoded redirect URL or null
 */
export const getRedirectFromParams = (searchParams) => {
    try {
        const params = typeof searchParams === 'string' 
            ? new URLSearchParams(searchParams)
            : searchParams;
            
        const encodedRedirect = params.get('redirect');
        if (!encodedRedirect) return null;
        
        const redirect = decodeURIComponent(encodedRedirect);
        return isValidRedirect(redirect) ? redirect : null;
    } catch (error) {
        console.error('Error parsing redirect from params:', error);
        return null;
    }
};

/**
 * Creates a URL with redirect parameter
 * @param {string} baseUrl - The base URL
 * @param {string} redirectUrl - The redirect URL to encode
 * @returns {string} - The complete URL with redirect parameter
 */
export const createUrlWithRedirect = (baseUrl, redirectUrl) => {
    if (!isValidRedirect(redirectUrl)) {
        console.warn('Invalid redirect URL, returning base URL:', redirectUrl);
        return baseUrl;
    }
    
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}redirect=${encodeURIComponent(redirectUrl)}`;
};